import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  Table,
  Button,
  Tag,
  Row,
  Col,
  Icon,
  Input,
  Radio,
  Typography,
  Popconfirm
} from 'antd'
import moment from 'moment'
import numeral from 'numeral'

import { getPriceDashboard, fetchPriceNow } from '../../modules/priceDashboard'
import HistoryModal from './components/HistoryModal'
import PriceCardGrid from './components/PriceCardGrid'

const { Title, Text } = Typography

const statCardStyle = {
  minHeight: 120
}

const iconStyle = {
  fontSize: 40,
  color: '#1890ff'
}

const formatPrice = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return numeral(value).format('0,0.00')
}

const formatDateTime = (value) => {
  if (!value) return '-'
  // Backend timestamps are UTC (datetime.utcnow); pin display to Asia/Bangkok (UTC+7).
  return moment.utc(value).utcOffset(7).format('YYYY-MM-DD HH:mm:ss')
}

const formatPair = (currency, unit) => {
  const parts = [currency, unit].filter(Boolean)
  return parts.length ? parts.join('/') : ''
}

const numericValue = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(value)) {
    return null
  }
  return Number(value)
}

const compareNullableNumbers = (a, b) => {
  const left = numericValue(a)
  const right = numericValue(b)
  if (left === null && right === null) return 0
  if (left === null) return -1
  if (right === null) return 1
  return left - right
}

const compareDates = (a, b) => {
  const left = a ? moment(a).valueOf() : null
  const right = b ? moment(b).valueOf() : null
  if (left === null && right === null) return 0
  if (left === null) return -1
  if (right === null) return 1
  return left - right
}

const statusRank = (status) => {
  const upper = status ? String(status).toUpperCase() : 'UNKNOWN'
  const rank = {
    SUCCESS: 1,
    RETRY: 2,
    TIMEOUT: 3,
    BLOCKED: 4,
    FAILED: 5,
    UNKNOWN: 6
  }
  return rank[upper] || rank.UNKNOWN
}

const renderPriceWithUnit = (value, currency, unit, opts) => {
  if (value === null || value === undefined || isNaN(value)) return '-'
  const pair = formatPair(currency, unit)
  const secondary = opts && opts.secondary
  return (
    <span style={secondary ? { color: '#8c8c8c' } : undefined}>
      {formatPrice(value)}
      {pair && (
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
          {pair}
        </Text>
      )}
    </span>
  )
}

// Primary = target (converted, what the user reads first); source shown as
// secondary fallback. % change is computed elsewhere from the same primary pair.
const renderSourceTarget = (sourceValue, targetValue, record) => {
  const hasSource =
    sourceValue !== null && sourceValue !== undefined && !isNaN(sourceValue)
  const hasTarget =
    targetValue !== null && targetValue !== undefined && !isNaN(targetValue)
  if (!hasSource && !hasTarget) return '-'

  if (hasTarget) {
    return (
      <div>
        <div>
          {renderPriceWithUnit(
            targetValue,
            record.target_currency,
            record.target_unit
          )}
        </div>
        {hasSource && (
          <div style={{ fontSize: 12, marginTop: 2 }}>
            {renderPriceWithUnit(
              sourceValue,
              record.source_currency,
              record.source_unit,
              { secondary: true }
            )}
          </div>
        )}
      </div>
    )
  }
  // No target → show source as the only value (STOCK/ECONOMIC/OTHER).
  return (
    <div>
      {renderPriceWithUnit(
        sourceValue,
        record.source_currency,
        record.source_unit
      )}
    </div>
  )
}

// Primary pair for % change + sorter: pick target only if BOTH ends exist,
// otherwise fall back to the source pair (with previous_price as a final
// fallback for closing). Mirrors Discord's `change_pct` in app/services/discord.py
// so the % shown in admin matches what Discord posts.
const primaryPair = (record) => {
  const hasTargetPair =
    record.target_current_price != null &&
    record.target_closing_price != null
  if (hasTargetPair) {
    return {
      current: record.target_current_price,
      closing: record.target_closing_price
    }
  }
  return {
    current: record.current_price,
    closing:
      record.closing_price != null
        ? record.closing_price
        : record.previous_price
  }
}

const getChange = (current, closing) => {
  const currentNumber = numericValue(current)
  const closingNumber = numericValue(closing)
  if (currentNumber === null || closingNumber === null || closingNumber === 0) {
    return null
  }
  return ((currentNumber - closingNumber) / Math.abs(closingNumber)) * 100
}

const renderChange = (current, closing) => {
  const change = getChange(current, closing)
  if (change === null) return <Text type="secondary">-</Text>

  let color = '#8c8c8c'
  let arrow = null
  if (change > 0) {
    color = '#52c41a'
    arrow = <Icon type="arrow-up" />
  } else if (change < 0) {
    color = '#f5222d'
    arrow = <Icon type="arrow-down" />
  }

  return (
    <span style={{ color }}>
      {arrow} {numeral(Math.abs(change)).format('0,0.00')}%
    </span>
  )
}

const renderStatusTag = (status) => {
  if (!status) return <Tag>UNKNOWN</Tag>
  const upper = String(status).toUpperCase()
  const colorMap = {
    SUCCESS: 'green',
    FAILED: 'red',
    BLOCKED: 'orange',
    TIMEOUT: 'default'
  }
  return <Tag color={colorMap[upper]}>{upper}</Tag>
}

const filterByName = (items, query) => {
  if (!query || !query.trim()) return items || []
  const q = query.trim().toLowerCase()
  return (items || []).filter(
    (p) =>
      (p.name && p.name.toLowerCase().includes(q)) ||
      (p.name_en && p.name_en.toLowerCase().includes(q))
  )
}

const PriceDashboard = () => {
  const dispatch = useDispatch()
  const [search, setSearch] = useState('')
  const [historyRecord, setHistoryRecord] = useState(null)
  const [viewMode, setViewMode] = useState('list')

  const { dashboard, isLoading, fetchingId } = useSelector(
    (state) => ({
      dashboard: state.priceDashboard.dashboard,
      isLoading: state.priceDashboard.isLoading,
      fetchingId: state.priceDashboard.fetchingId
    }),
    []
  )

  useEffect(() => {
    dispatch(getPriceDashboard())
  }, [dispatch])

  const filteredPrices = useMemo(
    () => filterByName(dashboard.recent_prices, search),
    [dashboard.recent_prices, search]
  )
  const filteredFailed = useMemo(
    () => filterByName(dashboard.failed_items, search),
    [dashboard.failed_items, search]
  )

  const onReload = () => {
    dispatch(getPriceDashboard())
  }

  const onFetchNow = async (id) => {
    await dispatch(fetchPriceNow(id))
    await dispatch(getPriceDashboard())
  }

  const columns = [
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <div>{name}</div>
          {record.name_en && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.name_en}
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'ราคาล่าสุด',
      key: 'current_price',
      align: 'right',
      render: (text, record) =>
        renderSourceTarget(
          record.current_price,
          record.target_current_price,
          record
        )
    },
    {
      title: 'ราคาปิด',
      key: 'closing_price',
      align: 'right',
      render: (text, record) =>
        renderSourceTarget(
          record.closing_price,
          record.target_closing_price,
          record
        )
    },
    {
      title: '% เปลี่ยนแปลง',
      key: 'change',
      align: 'right',
      sorter: (a, b) => {
        const aP = primaryPair(a)
        const bP = primaryPair(b)
        return compareNullableNumbers(
          getChange(aP.current, aP.closing),
          getChange(bP.current, bP.closing)
        )
      },
      render: (text, record) => {
        const p = primaryPair(record)
        return renderChange(p.current, p.closing)
      }
    },
    {
      title: 'ดึงล่าสุด',
      dataIndex: 'last_fetched_at',
      key: 'last_fetched_at',
      sorter: (a, b) => compareDates(a.last_fetched_at, b.last_fetched_at),
      defaultSortOrder: 'descend',
      render: formatDateTime
    },
    {
      title: 'สถานะ',
      key: 'status',
      sorter: (a, b) =>
        statusRank(a.last_status) - statusRank(b.last_status) ||
        String(a.last_status || '').localeCompare(String(b.last_status || '')),
      render: (text, record) => (
        <div>
          {renderStatusTag(record.last_status)}
          {record.last_status === 'FAILED' && record.last_message && (
            <div
              style={{
                color: '#f5222d',
                fontSize: 12,
                marginTop: 4,
                maxWidth: 240,
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}>
              {record.last_message}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 180,
      render: (text, record) => (
        <span>
          <Button
            size="small"
            icon="line-chart"
            style={{ marginRight: 6 }}
            onClick={() => setHistoryRecord(record)}>
            ประวัติ
          </Button>
          <Popconfirm
            title="ดึงราคาทันที?"
            onConfirm={() => onFetchNow(record.id)}>
            <Button
              type="primary"
              size="small"
              icon="reload"
              loading={fetchingId === record.id}>
              Fetch Now
            </Button>
          </Popconfirm>
        </span>
      )
    }
  ]

  const failedColumns = [
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'ข้อผิดพลาด',
      dataIndex: 'last_message',
      key: 'last_message',
      render: (err) => <Tag color="red">{err || 'UNKNOWN'}</Tag>
    },
    {
      title: 'ครั้งล่าสุด',
      dataIndex: 'last_fetched_at',
      key: 'last_fetched_at',
      render: formatDateTime
    },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      render: (text, record) => (
        <span>
          <Button
            size="small"
            icon="line-chart"
            style={{ marginRight: 6 }}
            onClick={() => setHistoryRecord(record)}>
            ประวัติ
          </Button>
          <Popconfirm
            title="ดึงราคาทันที?"
            onConfirm={() => onFetchNow(record.id)}>
            <Button
              type="primary"
              size="small"
              icon="reload"
              loading={fetchingId === record.id}>
              Retry
            </Button>
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <Title level={3} style={{ margin: 0 }}>
          Price Bot Dashboard
        </Title>
        <Button icon="reload" onClick={onReload} loading={isLoading}>
          Reload
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={statCardStyle} loading={isLoading}>
            <Card.Meta
              avatar={<Icon type="database" style={iconStyle} />}
              title={
                <Title level={2} style={{ margin: 0 }}>
                  {dashboard.total_prices || 0}
                </Title>
              }
              description="Total Items"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={statCardStyle} loading={isLoading}>
            <Card.Meta
              avatar={
                <Icon
                  type="check-circle"
                  style={{ ...iconStyle, color: '#52c41a' }}
                />
              }
              title={
                <Title level={2} style={{ margin: 0 }}>
                  {dashboard.active_prices || 0}
                </Title>
              }
              description="Active Items"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={statCardStyle} loading={isLoading}>
            <Card.Meta
              avatar={
                <Icon
                  type="line-chart"
                  style={{ ...iconStyle, color: '#faad14' }}
                />
              }
              title={
                <Title level={2} style={{ margin: 0 }}>
                  {numeral(dashboard.success_rate || 0).format('0,0.00')}%
                </Title>
              }
              description="Success Rate (24h)"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={statCardStyle} loading={isLoading}>
            <Card.Meta
              avatar={
                <Icon
                  type="close-circle"
                  style={{ ...iconStyle, color: '#f5222d' }}
                />
              }
              title={
                <Title level={2} style={{ margin: 0 }}>
                  {(dashboard.failed_items || []).length}
                </Title>
              }
              description="Failed Items"
            />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: '8px 16px' }}>
        <Text type="secondary">
          <Icon type="clock-circle" /> Last scrape:{' '}
          {formatDateTime(dashboard.last_scrape_time)}
        </Text>
      </Card>

      {(dashboard.failed_items || []).length > 0 && (
        <Card title="Failed Items" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            rowKey={(record) => record.id}
            dataSource={filteredFailed}
            columns={failedColumns}
            pagination={false}
          />
        </Card>
      )}

      <Card
        title="Recent Prices"
        extra={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Radio.Group
              size="small"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}>
              <Radio.Button value="list">
                <Icon type="bars" /> List
              </Radio.Button>
              <Radio.Button value="card">
                <Icon type="appstore" /> Card
              </Radio.Button>
            </Radio.Group>
            <Input.Search
              placeholder="ค้นหาชื่อดัชนี (ไทย / EN)"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 280 }}
            />
          </span>
        }>
        {viewMode === 'card' ? (
          <PriceCardGrid
            items={filteredPrices}
            loading={isLoading}
            onCardClick={(record) => setHistoryRecord(record)}
          />
        ) : (
          <Table
            loading={isLoading}
            scroll={{ x: 1200 }}
            rowKey={(record) => record.id}
            dataSource={filteredPrices}
            columns={columns}
            pagination={{ pageSize: 20 }}
          />
        )}
      </Card>

      <HistoryModal
        visible={!!historyRecord}
        record={historyRecord}
        onClose={() => setHistoryRecord(null)}
      />
    </div>
  )
}

export default PriceDashboard
